import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  LinearProgress,
  Breadcrumbs,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
} from '@mui/material';
import {
  Assignment,
  PlayArrow,
  Visibility,
  QrCode,
  CheckCircle,
  Schedule,
  Warning,
  Business,
  Print,
  Edit,
  LocationOn,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth, UserRole } from '../contexts/AuthContext';
import samplesService, { Sample, SampleStatus, Priority } from '../services/samplesService';
import usersService, { User } from '../services/usersService';

const Reception: React.FC = () => {
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const [samples, setSamples] = useState<Sample[]>([]);
  const [technicians, setTechnicians] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
  const [transitionDialog, setTransitionDialog] = useState(false);
  const [comments, setComments] = useState('');
  const [assignedTechnician, setAssignedTechnician] = useState('');
  const [preparationMethod, setPreparationMethod] = useState('');
  const [estimatedDuration, setEstimatedDuration] = useState('');
  const [priorityOverride, setPriorityOverride] = useState(false);
  const [newPriority, setNewPriority] = useState<Priority>(Priority.NORMALE);
  const [storageLocation, setStorageLocation] = useState('');

  useEffect(() => {
    loadReceptionData();
  }, []);

  const loadReceptionData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load samples and technicians in parallel
      const [samplesResponse, techniciansData] = await Promise.all([
        samplesService.getSamples(1, 100, {
          status: [SampleStatus.RECEPTION]
        }),
        usersService.getTechnicians()
      ]);
      
      setSamples(samplesResponse.samples);
      setTechnicians(techniciansData);
    } catch (error) {
      console.error('Error loading reception data:', error);
      setError('Erreur lors du chargement des données de réception');
    } finally {
      setLoading(false);
    }
  };

  const handleStartPreparation = async (sample: Sample) => {
    try {
      // Update sample status with detailed information
      const updateData = {
        statut: SampleStatus.PREPARATION_MECANIQUE,
        assigned_to: assignedTechnician,
        localisation_stockage: storageLocation || 'Atelier Préparation - P1',
        priorite: priorityOverride ? newPriority : sample.priorite,
        metadonnees: {
          ...sample.metadonnees,
          methode_preparation_prevue: preparationMethod,
          duree_estimee_heures: estimatedDuration,
          technicien_assigne: technicians.find(t => t.id === assignedTechnician)?.firstName + ' ' + technicians.find(t => t.id === assignedTechnician)?.lastName,
          date_debut_preparation: new Date().toISOString(),
          commentaires_reception: comments,
        }
      };

      await samplesService.updateSample(sample.id, updateData);
      
      setSuccessMessage(`Échantillon ${sample.sample_id} transféré en préparation avec succès`);
      setTransitionDialog(false);
      resetDialogState();
      loadReceptionData();
    } catch (error) {
      console.error('Error updating sample status:', error);
      setError('Erreur lors de la mise à jour du statut');
    }
  };

  const resetDialogState = () => {
    setSelectedSample(null);
    setComments('');
    setAssignedTechnician('');
    setPreparationMethod('');
    setEstimatedDuration('');
    setPriorityOverride(false);
    setNewPriority(Priority.NORMALE);
    setStorageLocation('');
  };

  const handlePrintLabel = (sample: Sample) => {
    // In a real app, this would generate and print a label

    setSuccessMessage(`Étiquette imprimée pour l'échantillon ${sample.sample_id}`);
  };

  const handleEditSample = (sample: Sample) => {
    navigate(`/samples/${sample.id}`);
  };

  const getPriorityColor = (priority: Priority) => {
    switch (priority) {
      case Priority.URGENTE: return 'error';
      case Priority.ELEVEE: return 'warning';
      case Priority.NORMALE: return 'info';
      default: return 'default';
    }
  };

  const getPriorityLabel = (priority: Priority) => {
    switch (priority) {
      case Priority.URGENTE: return 'Urgente';
      case Priority.ELEVEE: return 'Élevée';
      case Priority.NORMALE: return 'Normale';
      default: return priority;
    }
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getWaitingTime = (sample: Sample) => {
    if (!sample.date_reception) return 0;
    return Math.floor((Date.now() - new Date(sample.date_reception).getTime()) / (1000 * 60 * 60));
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Chargement des échantillons en réception...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link color="inherit" href="#" onClick={() => navigate('/dashboard')}>
          Tableau de Bord
        </Link>
        <Typography color="text.primary">Réception</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Réception des Échantillons
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestion des échantillons en attente de traitement - {samples.length} échantillons
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Assignment />}
            onClick={() => navigate('/samples')}
          >
            Tous les Échantillons
          </Button>
          <Button
            variant="contained"
            startIcon={<QrCode />}
            onClick={() => navigate('/qr-scanner')}
          >
            Scanner QR Code
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Total en Réception
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {samples.length}
                  </Typography>
                  <Typography variant="caption" color="primary.main">
                    Échantillons à traiter
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <Assignment />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Urgents
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="error.main">
                    {samples.filter(s => s.priorite === Priority.URGENTE).length}
                  </Typography>
                  <Typography variant="caption" color="error.main">
                    Traitement prioritaire
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'error.main' }}>
                  <Warning />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Reçus Aujourd'hui
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {samples.filter(s => {
                      if (!s.date_reception) return false;
                      const today = new Date();
                      const sampleDate = new Date(s.date_reception);
                      return sampleDate.toDateString() === today.toDateString();
                    }).length}
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    Nouveaux échantillons
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <CheckCircle />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    En Attente plus de 4h
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {samples.filter(s => {
                      if (!s.date_reception) return false;
                      const hoursWaiting = (Date.now() - new Date(s.date_reception).getTime()) / (1000 * 60 * 60);
                      return hoursWaiting > 4;
                    }).length}
                  </Typography>
                  <Typography variant="caption" color="warning.main">
                    Attention requise
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <Schedule />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Samples Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Échantillons en Réception ({samples.length})
          </Typography>
          
          {samples.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Assignment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Aucun échantillon en réception
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tous les échantillons ont été traités ou aucun échantillon n'a été reçu.
              </Typography>
              <Button
                variant="contained"
                startIcon={<QrCode />}
                onClick={() => navigate('/qr-scanner')}
                sx={{ mt: 2 }}
              >
                Scanner un Nouvel Échantillon
              </Button>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Échantillon</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Projet</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Priorité</TableCell>
                    <TableCell>Date Réception</TableCell>
                    <TableCell>Temps d'Attente</TableCell>
                    <TableCell>Localisation</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {samples.map((sample) => {
                    const hoursWaiting = getWaitingTime(sample);
                    
                    return (
                      <TableRow key={sample.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                              {sample.sample_id.split('-')[2]?.slice(-2) || 'XX'}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {sample.sample_id}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {sample.external_id || 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Business sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Box>
                              <Typography variant="body2">
                                {sample.client?.company_name || 'N/A'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {sample.client?.contact_person || ''}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {sample.nom_projet || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {sample.site_minier || ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={sample.type_echantillon}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getPriorityLabel(sample.priorite)}
                            size="small"
                            color={getPriorityColor(sample.priorite)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {sample.date_reception ? formatDate(sample.date_reception) : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Schedule sx={{ 
                              fontSize: 16, 
                              color: hoursWaiting > 4 ? 'warning.main' : 'text.secondary' 
                            }} />
                            <Typography 
                              variant="body2"
                              color={hoursWaiting > 4 ? 'warning.main' : 'text.primary'}
                              fontWeight={hoursWaiting > 4 ? 'bold' : 'normal'}
                            >
                              {hoursWaiting}h
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2">
                              {sample.localisation_stockage || 'Zone Réception'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/samples/${sample.id}`)}
                              title="Voir détails"
                            >
                              <Visibility />
                            </IconButton>
                            {hasRole([UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.SAMPLE_RECEIVER]) && (
                              <IconButton
                                size="small"
                                onClick={() => handleEditSample(sample)}
                                title="Modifier échantillon"
                              >
                                <Edit />
                              </IconButton>
                            )}
                            {hasRole([UserRole.ADMIN, UserRole.LABORATORY_MANAGER, UserRole.SAMPLE_RECEIVER, UserRole.TECHNICIAN_PREP]) && (
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => {
                                  setSelectedSample(sample);
                                  setTransitionDialog(true);
                                }}
                                title="Commencer préparation"
                              >
                                <PlayArrow />
                              </IconButton>
                            )}
                            <IconButton
                              size="small"
                              onClick={() => handlePrintLabel(sample)}
                              title="Imprimer étiquette"
                            >
                              <Print />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Advanced Transition Dialog */}
      <Dialog open={transitionDialog} onClose={() => setTransitionDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PlayArrow color="primary" />
            Commencer la Préparation Mécanique
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedSample && (
            <>
              {/* Sample Information */}
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Informations de l'Échantillon
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Code Échantillon</Typography>
                      <Typography variant="body1" fontWeight="medium">{selectedSample.sample_id}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Client</Typography>
                      <Typography variant="body1">{selectedSample.client?.company_name}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Projet</Typography>
                      <Typography variant="body1">{selectedSample.nom_projet || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Type</Typography>
                      <Typography variant="body1">{selectedSample.type_echantillon}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Priorité Actuelle</Typography>
                      <Chip 
                        label={getPriorityLabel(selectedSample.priorite)} 
                        size="small" 
                        color={getPriorityColor(selectedSample.priorite)} 
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Temps d'Attente</Typography>
                      <Typography variant="body1" color={getWaitingTime(selectedSample) > 4 ? 'warning.main' : 'text.primary'}>
                        {getWaitingTime(selectedSample)} heures
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Divider sx={{ my: 2 }} />

              {/* Assignment and Configuration */}
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Technicien Assigné</InputLabel>
                    <Select
                      value={assignedTechnician}
                      onChange={(e) => setAssignedTechnician(e.target.value)}
                      label="Technicien Assigné"
                    >
                      {technicians.map((tech) => (
                        <MenuItem key={tech.id} value={tech.id}>
                          <Box>
                            <Typography variant="body2">
                              {tech.firstName} {tech.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {tech.department || 'Préparation Mécanique'}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Méthode de Préparation</InputLabel>
                    <Select
                      value={preparationMethod}
                      onChange={(e) => setPreparationMethod(e.target.value)}
                      label="Méthode de Préparation"
                    >
                      <MenuItem value="BROYAGE_STANDARD">Broyage Standard (moins de 2mm)</MenuItem>
                      <MenuItem value="BROYAGE_FIN">Broyage Fin (moins de 75μm)</MenuItem>
                      <MenuItem value="CONCASSAGE">Concassage (moins de 10mm)</MenuItem>
                      <MenuItem value="TAMISAGE">Tamisage Granulométrique</MenuItem>
                      <MenuItem value="QUARTAGE">Quartage et Homogénéisation</MenuItem>
                      <MenuItem value="SECHAGE">Séchage (105°C)</MenuItem>
                      <MenuItem value="PULVERISATION">Pulvérisation (moins de 45μm)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Durée Estimée</InputLabel>
                    <Select
                      value={estimatedDuration}
                      onChange={(e) => setEstimatedDuration(e.target.value)}
                      label="Durée Estimée"
                    >
                      <MenuItem value="1">1 heure</MenuItem>
                      <MenuItem value="2">2 heures</MenuItem>
                      <MenuItem value="4">4 heures</MenuItem>
                      <MenuItem value="8">8 heures (1 jour)</MenuItem>
                      <MenuItem value="16">16 heures (2 jours)</MenuItem>
                      <MenuItem value="24">24 heures (3 jours)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Localisation de Stockage"
                    value={storageLocation}
                    onChange={(e) => setStorageLocation(e.target.value)}
                    placeholder="ex: Atelier Préparation - P1-A-015"
                    helperText="Localisation pendant la préparation"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={priorityOverride}
                        onChange={(e) => setPriorityOverride(e.target.checked)}
                      />
                    }
                    label="Modifier la priorité de l'échantillon"
                  />
                </Grid>

                {priorityOverride && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Nouvelle Priorité</InputLabel>
                      <Select
                        value={newPriority}
                        onChange={(e) => setNewPriority(e.target.value as Priority)}
                        label="Nouvelle Priorité"
                      >
                        <MenuItem value={Priority.URGENTE}>
                          <Chip label="Urgente" size="small" color="error" />
                        </MenuItem>
                        <MenuItem value={Priority.ELEVEE}>
                          <Chip label="Élevée" size="small" color="warning" />
                        </MenuItem>
                        <MenuItem value={Priority.NORMALE}>
                          <Chip label="Normale" size="small" color="info" />
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Commentaires et Instructions"
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    placeholder="Ajouter des commentaires sur la préparation, instructions spéciales, observations..."
                  />
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => {
            setTransitionDialog(false);
            resetDialogState();
          }}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={() => selectedSample && handleStartPreparation(selectedSample)}
            startIcon={<PlayArrow />}
            disabled={!assignedTechnician || !preparationMethod}
            size="large"
          >
            Commencer la Préparation
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Reception; 